import React, { useState } from "react";
import { useDarkModeSetting } from "../hooks/useDarkModeSetting";
import { THUMBNAIL_WIDTH } from "../hooks/usePageThumbnails";

/**
 * The page strip: each page as a small sheet of paper, drawn by the same
 * renderer as the exports. Page actions appear on hover/focus.
 */
const PageNavigator = ({
  pages,
  panelCounts = {},
  unit = "panel",
  thumbnails = {},
  currentPageIndex,
  onSelectPage,
  onAddPage,
  onDeletePage,
  onDuplicatePage,
  onMovePage,
  onExportAll,
  exportAllLabel = "Export comic (PDF)",
  exportProgress,
}) => {
  const darkMode = useDarkModeSetting();
  const [activeId, setActiveId] = useState(null);

  const ink = darkMode ? "#e8e2d5" : "#1a1a1a";
  const muted = darkMode ? "#9a917f" : "#6b6455";
  const paper = darkMode ? "#2a251e" : "#fffdf6";
  const edge = darkMode ? "#4a4238" : "#8a8171";

  const actionButton = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    padding: 0,
    border: `1px solid ${ink}`,
    borderRadius: 0,
    background: paper,
    color: ink,
    cursor: "pointer",
    boxShadow: "1px 1px 0 rgba(0,0,0,0.25)",
  };

  const footerButton = {
    width: "100%",
    padding: "8px 0",
    background: "transparent",
    border: `1px dashed ${darkMode ? "#6b6151" : "#8a8171"}`,
    borderRadius: 0,
    cursor: "pointer",
    fontSize: 11,
    color: ink,
    fontWeight: 700,
    fontFamily: "'Georgia', serif",
  };

  return (
    <div
      style={{
        width: 120,
        minWidth: 120,
        background: darkMode ? "#1c1813" : "#efe9df",
        borderRight: `2px solid ${darkMode ? "#4a4238" : "#1a1a1a"}`,
        color: ink,
        overflowY: "auto",
        padding: "8px 0 12px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        fontFamily: "'Georgia', serif",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: muted,
          textAlign: "center",
          padding: "4px 0 0",
          textTransform: "uppercase",
          letterSpacing: 1.5,
        }}
      >
        Pages ({pages.length})
      </div>

      {pages.map((page, index) => {
        const isCurrent = index === currentPageIndex;
        const thumb = thumbnails[page.id];
        const count = panelCounts[page.id];
        const showActions = activeId === page.id;
        const label = `Page ${index + 1}${count != null ? ` · ${count} ${count === 1 ? unit : `${unit}s`}` : ""}`;
        return (
          <div
            key={page.id}
            style={{ position: "relative", width: THUMBNAIL_WIDTH }}
            onPointerEnter={() => setActiveId(page.id)}
            onPointerLeave={() => setActiveId((id) => (id === page.id ? null : id))}
            onFocus={() => setActiveId(page.id)}
          >
            <button
              type="button"
              aria-label={label}
              aria-current={isCurrent ? "page" : undefined}
              title={label}
              onClick={() => onSelectPage(index)}
              style={{
                display: "block",
                width: THUMBNAIL_WIDTH,
                height: Math.round(THUMBNAIL_WIDTH * (thumb?.aspect || 11 / 8.5)),
                padding: 0,
                border: "none",
                background: paper,
                cursor: "pointer",
                outline: isCurrent ? "2px solid #00a4e4" : "none",
                outlineOffset: 3,
                boxShadow: isCurrent
                  ? "0 6px 14px rgba(0,0,0,0.28)"
                  : "0 2px 5px rgba(0,0,0,0.22)",
                transform: isCurrent ? "translateY(-1px)" : undefined,
                transition: "box-shadow 160ms ease, transform 160ms ease",
              }}
            >
              {thumb && (
                <img
                  src={thumb.url}
                  alt=""
                  draggable={false}
                  style={{ display: "block", width: "100%", height: "100%" }}
                />
              )}
            </button>
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                fontWeight: isCurrent ? 700 : 400,
                textAlign: "center",
                color: isCurrent ? ink : muted,
              }}
            >
              {index + 1}
            </div>

            {showActions && (
              <div
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {index > 0 && (
                  <button type="button" title="Move up" aria-label={`Move page ${index + 1} up`}
                    onClick={() => onMovePage(index, index - 1)} style={actionButton}>
                    <span className="material-icons" aria-hidden="true" style={{ fontSize: 13 }}>arrow_upward</span>
                  </button>
                )}
                {index < pages.length - 1 && (
                  <button type="button" title="Move down" aria-label={`Move page ${index + 1} down`}
                    onClick={() => onMovePage(index, index + 1)} style={actionButton}>
                    <span className="material-icons" aria-hidden="true" style={{ fontSize: 13 }}>arrow_downward</span>
                  </button>
                )}
                <button type="button" title="Duplicate page" aria-label={`Duplicate page ${index + 1}`}
                  onClick={() => onDuplicatePage(index)} style={actionButton}>
                  <span className="material-icons" aria-hidden="true" style={{ fontSize: 13 }}>content_copy</span>
                </button>
                {pages.length > 1 && (
                  <button type="button" title="Delete page" aria-label={`Delete page ${index + 1}`}
                    onClick={() => {
                      if (window.confirm(`Delete page ${index + 1}?`)) onDeletePage(index);
                    }}
                    style={{ ...actionButton, color: darkMode ? "#f08c7d" : "#b42318" }}>
                    <span className="material-icons" aria-hidden="true" style={{ fontSize: 13 }}>close</span>
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div style={{ width: THUMBNAIL_WIDTH, display: "flex", flexDirection: "column", gap: 8 }}>
        <button type="button" onClick={onAddPage} style={footerButton}>
          + Add Page
        </button>
        {onExportAll && (
          <button
            type="button"
            onClick={onExportAll}
            disabled={Boolean(exportProgress)}
            title="Export every page as one print-ready PDF (300 DPI)"
            style={{ ...footerButton, borderStyle: "solid", cursor: exportProgress ? "progress" : "pointer" }}
          >
            {exportProgress
              ? `Exporting ${exportProgress.page || 1}/${exportProgress.total}…`
              : exportAllLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default PageNavigator;
